import { getApiConfig } from '@/api/config/client/getApiConfig';
import { NotFoundError } from '@/api/errors';
import { EntitlementHeaders } from '@/api/headers';
import { ApiLogger } from '@/api/logger/apiLogger';
import { osDetector } from '@/api/osDetector/client';
import { parseHomeLocation, parseHomeLocationMetadata } from '@/api/parsers/parseHomeLocation';
import { parseUserPreference } from '@/api/parsers/parseUserPreference';
import { type HomeLocation, type HomeLocationResponse } from '@/api/types/homeLocationTypes';
import { type PreferencesResponse, type UserPreference } from '@/api/types/userPreferenceTypes';

import { HttpClient, type HttpOptions } from './httpClient';

const apiConfig = getApiConfig();
const { apiKey, isLogEnabled, entitlementBaseUrl } = apiConfig;

/**
 * User Preferences API Client
 * @see https://teamfox.atlassian.net/wiki/spaces/FOXDTC/pages/2360246435/User+Preferences+API+Spec
 */
export class PreferencesClient {
  public constructor(private httpClient: HttpClient) {}

  public async getUserPreference(options?: HttpOptions): Promise<UserPreference> {
    const response = await this.httpClient.get<PreferencesResponse>('/user-preferences/v1/preferences', options);

    const userPreference = parseUserPreference(response);

    return userPreference;
  }

  /**
   * Partially updates user preference(s), compares with {@link currentUserPreference} user preferences to enable update if given.
   */
  public async updateUserPreference(
    newUserPreference: Partial<UserPreference>,
    currentUserPreference?: UserPreference,
  ): Promise<Nullable<Partial<UserPreference>>> {
    const preferences = this.toApiData(newUserPreference, currentUserPreference);

    if (preferences.length > 0) {
      await this.httpClient.post('/user-preferences/v1/preferences', preferences);

      return newUserPreference;
    }

    return null;
  }

  public async getHomeLocation(options?: HttpOptions): Promise<HomeLocation | null> {
    try {
      const response = await this.httpClient.get<HomeLocationResponse>('/user-preferences/v1/home-location', options);

      const homeLocation = parseHomeLocation(response.results[0]);

      homeLocation.metadata = parseHomeLocationMetadata(response.metadata);

      return homeLocation;
    } catch (error) {
      if (error instanceof NotFoundError) {
        // we have to return null to prevent react query from making duplicated requests,
        // because react query does not cache when response is undefined or error, by returning null react query can cache the response
        return null;
      }

      throw error;
    }
  }

  /**
   * Updates the user's home location.
   * @returns The updated home location response or undefined if no update was done (given {@link zipCode} was the same as the current zip code).
   * @throws `BadRequestError` If given {@link zipCode} is invalid.
   * @throws `PreconditionFailedError` If user has reached the limit of updates.
   */
  public async updateHomeLocation(zipCode: string): Promise<HomeLocation | undefined> {
    const response = await this.httpClient.put<HomeLocationResponse>('/user-preferences/v1/home-location', {
      home_zip_code: zipCode,
    });

    if (response) {
      const homeLocation = parseHomeLocation(response.results[0]);

      return homeLocation;
    }
  }

  private toApiData(
    userPreference: Partial<UserPreference>,
    currentUserPreference?: UserPreference,
  ): { key: string; value: unknown }[] {
    const preferences: { key: string; value: unknown }[] = [];

    if (currentUserPreference) {
      if (userPreference.areas_interest && userPreference.areas_interest !== currentUserPreference.areas_interest) {
        preferences.push({ key: 'areas_interest', value: userPreference.areas_interest });
      }

      if (userPreference.home_location !== currentUserPreference.home_location) {
        preferences.push({ key: 'home_location', value: userPreference.home_location });
      }

      if (userPreference.location !== currentUserPreference.location) {
        preferences.push({ key: 'location', value: userPreference.location });
      }

      if (userPreference.no_spoiler_enabled !== currentUserPreference.no_spoiler_enabled) {
        preferences.push({ key: 'no_spoiler_enabled', value: userPreference.no_spoiler_enabled });
      }

      if (userPreference.onboarding_steps_completed !== currentUserPreference.onboarding_steps_completed) {
        const oldOnboardingStepsCompleted = currentUserPreference.onboarding_steps_completed;
        const updatedOnboardingStepsCompleted = userPreference.onboarding_steps_completed;

        if (updatedOnboardingStepsCompleted?.length) {
          const dedupedStepsCompleted = [
            ...new Set([...oldOnboardingStepsCompleted, ...updatedOnboardingStepsCompleted]),
          ];

          preferences.push({ key: 'onboarding_steps_completed', value: dedupedStepsCompleted });
        }
      }

      if (userPreference.playback_options !== currentUserPreference.playback_options) {
        preferences.push({ key: 'playback_options', value: userPreference.playback_options });
      }

      if (userPreference.total_changes !== currentUserPreference.total_changes) {
        preferences.push({ key: 'total_changes', value: userPreference.total_changes });
      }

      if (userPreference.braze_tune_in_fav !== currentUserPreference.braze_tune_in_fav) {
        preferences.push({ key: 'braze_tune_in_fav', value: userPreference.braze_tune_in_fav });
      }

      if (
        userPreference.braze_enable_web_push_notification !== currentUserPreference.braze_enable_web_push_notification
      ) {
        preferences.push({
          key: 'braze_enable_web_push_notification',
          value: userPreference.braze_enable_web_push_notification,
        });
      }

      return preferences;
    }

    // If no current preference, send all
    preferences.push(
      { key: 'areas_interest', value: userPreference.areas_interest },
      { key: 'home_location', value: userPreference.home_location },
      { key: 'location', value: userPreference.location },
      { key: 'no_spoiler_enabled', value: userPreference.no_spoiler_enabled },
      { key: 'onboarding_steps_completed', value: userPreference.onboarding_steps_completed },
      { key: 'playback_options', value: userPreference.playback_options },
      { key: 'total_changes', value: userPreference.total_changes },
      { key: 'braze_tune_in_fav', value: userPreference.braze_tune_in_fav },
      {
        key: 'braze_enable_web_push_notification',
        value: userPreference.braze_enable_web_push_notification,
      },
    );

    return preferences;
  }
}

const logger = new ApiLogger('PreferencesClient', isLogEnabled);
const headers = new EntitlementHeaders(apiKey);
const httpClient = new HttpClient({ apiConfig, baseUrl: entitlementBaseUrl, logger, headers, osDetector });

export const preferencesClient = new PreferencesClient(httpClient);
